/**
 * FarmPacks V2 — scroll reveal.
 * Adds `is-revealed` to [data-fp-reveal] elements as they enter the viewport.
 *
 * The pre-reveal state lives behind the `fp-reveal-ready` class on <html>, which
 * is only set once an observer is known to be working. If anything here fails —
 * no IntersectionObserver, a thrown constructor, reduced-motion — the class is
 * never applied (or is removed), so content renders plainly rather than
 * disappearing.
 */
(() => {
  const SELECTOR = '[data-fp-reveal]';
  const root = document.documentElement;

  const revealAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((el) => el.classList.add('is-revealed'));
  };

  const bailOut = () => {
    root.classList.remove('fp-reveal-ready');
    revealAll();
  };

  if (typeof IntersectionObserver !== 'function') {
    revealAll();
    return;
  }

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches) {
    revealAll();
    return;
  }

  let observer;
  try {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
  } catch (error) {
    revealAll();
    return;
  }

  // Anything already on screen (or scrolled past) when this runs has been
  // painted already. Marking it revealed before the pre-reveal class goes on
  // stops it blinking out and fading back in: on a phone this script runs a
  // couple of seconds after the first paint, so that blink read as the page
  // loading slowly. Only what is still below the fold animates in.
  try {
    const fold = window.innerHeight;
    const visible = Array.from(document.querySelectorAll(SELECTOR))
      .filter((el) => el.getBoundingClientRect().top < fold);
    visible.forEach((el) => el.classList.add('is-revealed'));
  } catch (error) {
    revealAll();
    return;
  }

  root.classList.add('fp-reveal-ready');

  const observe = (scope = document) => {
    try {
      scope.querySelectorAll(`${SELECTOR}:not(.is-revealed)`).forEach((el) => observer.observe(el));
    } catch (error) {
      bailOut();
    }
  };

  observe();

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', (event) => observe(event.target));

  reduced.addEventListener('change', (event) => {
    if (!event.matches) return;
    observer.disconnect();
    bailOut();
  });

  // Backstop: if an element is still hidden well after load, show it anyway.
  window.addEventListener('load', () => {
    setTimeout(() => {
      document.querySelectorAll(`${SELECTOR}:not(.is-revealed)`).forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-revealed');
      });
    }, 1200);
  });
})();
